import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, UserPlus, PackageCheck, Network } from 'lucide-react';
import { useContact } from '../context/ContactContext';
import { COMPANY } from '../data/company';

// Reusable lead-generation CTAs used across every page.
export const CTAButtons = ({ variant = 'default', className = '' }) => {
  const { openContactForm } = useContact();
  const onLight = variant === 'onLight';

  return (
    <div className={`flex flex-col sm:flex-row flex-wrap gap-4 ${className}`}>
      <Button
        size="lg"
        onClick={openContactForm}
        data-testid="cta-request-dispatcher"
        className="bg-orange-600 hover:bg-orange-700 text-white text-base px-6 py-4 flex items-center gap-2"
      >
        <UserPlus className="h-5 w-5" />
        Request a Dispatcher
      </Button>
      <Button
        size="lg"
        onClick={() => (window.location.href = COMPANY.phoneHref)}
        data-testid="cta-schedule-call"
        variant="outline"
        className={
          onLight
            ? 'border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white text-base px-6 py-4 flex items-center gap-2'
            : 'border-white text-white hover:bg-white hover:text-blue-900 text-base px-6 py-4 flex items-center gap-2 bg-transparent'
        }
      >
        <Phone className="h-5 w-5" />
        Schedule a Call
      </Button>
    </div>
  );
};

// Full-width conversion banner with all four lead-gen actions.
export const CTASection = ({ title, subtitle }) => {
  const { openContactForm } = useContact();
  const navigate = useNavigate();

  const actions = [
    { label: 'Schedule a Call', icon: Phone, onClick: () => (window.location.href = COMPANY.phoneHref), testid: 'cta-banner-schedule-call' },
    { label: 'Request a Dispatcher', icon: UserPlus, onClick: openContactForm, testid: 'cta-banner-request-dispatcher' },
    { label: 'Get Load Assistance', icon: PackageCheck, onClick: openContactForm, testid: 'cta-banner-load-assistance' },
    { label: 'Join Our Carrier Network', icon: Network, onClick: () => navigate('/register'), testid: 'cta-banner-join-network' },
  ];

  return (
    <section className="bg-blue-900 py-16" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {title || 'Ready to Keep Your Wheels Turning?'}
        </h2>
        <p className="text-lg text-blue-100 mb-10 max-w-2xl mx-auto">
          {subtitle || `Talk to a real dispatcher today. Call ${COMPANY.phoneDisplay} or pick the option that fits you best.`}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {actions.map((a) => (
            <button
              key={a.label}
              onClick={a.onClick}
              data-testid={a.testid}
              className="group flex flex-col items-center gap-3 rounded-xl bg-blue-800/60 hover:bg-orange-600 border border-blue-700 hover:border-orange-500 p-6 text-white"
            >
              <a.icon className="h-7 w-7 text-orange-400 group-hover:text-white" />
              <span className="font-semibold text-sm">{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
